import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Save as SaveIcon,
  Business as BusinessIcon,
  ContactPhone as ContactPhoneIcon,
  AutoAwesome as MagicIcon,
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';

// Import custom components
import { Card, MagicLoader } from '../../design-system';
import { FormPageHeader } from '../../components/ui/FormFields';

// Import enhanced form stepper
import { FormStepperEnhanced, FormStep } from '../../components/shared/forms';

// Import client form sections
import ClientBasicInfoForm from './forms/ClientBasicInfoForm';
import ClientContactForm from './forms/ClientContactForm';
import ClientMagicalElementsForm from './forms/ClientMagicalElementsForm';

// Import hooks and services
import useClientStore from '../../stores/clientStore';
import useNotificationStore from '../../stores/notificationStore';

// Initial client data
const initialClientData = {
  // Basic Information
  name: '',
  code: '',
  description: '',
  industry: '',
  region: 'Egypt',
  status: 'onboarding',
  subscriptionTier: 'standard',
  
  // Contact Information
  contacts: [
    {
      name: '',
      position: '',
      email: '',
      phone: '',
      isPrimary: true,
    }
  ],
  address: {
    street: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'Egypt',
  },
  
  
  // Magical Elements
  magicalElements: {
    transformationNarrative: '',
    enchantmentLevel: 'moderate',
    specialFeatures: []
  }
};

// Validation schema
const getValidationSchema = (activeStep) => {
  // Basic information validation (Step 0)
  const basicInfoSchema = Yup.object({
    name: Yup.string().required('Client name is required'),
    industry: Yup.string().required('Industry is required'),
    region: Yup.string().required('Region is required'),
    status: Yup.string().required('Status is required'),
    subscriptionTier: Yup.string().required('Subscription tier is required')
  });
  
  // Contact information validation (Step 1)
  const contactSchema = Yup.object({
    contacts: Yup.array()
      .min(1, 'At least one contact is required')
      .of(
        Yup.object({
          name: Yup.string().required('Contact name is required'),
          email: Yup.string().email('Invalid email format')
        })
      )
  });
  
  
  // Switch based on active step
  switch (activeStep) {
    case 0:
      return basicInfoSchema;
    case 1:
      return contactSchema;
    default:
      return Yup.object({}); // No validation for magical elements step
  }
};

// Main ClientCreateEdit component
const ClientCreateEdit = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams(); // If id exists, we're in edit mode
  const isEditMode = Boolean(id);
  
  // Store hooks
  const { 
    currentClient, 
    isLoading, 
    error, 
    fetchClient,
    createClient,
    updateClient,
    uploadLogo
  } = useClientStore();
  
  const { showSuccess, showError } = useNotificationStore();
  
  // Local state
  const [activeStep, setActiveStep] = useState(0);
  const [clientData, setClientData] = useState(initialClientData);
  const [logoFile, setLogoFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Fetch client data if in edit mode
  useEffect(() => {
    if (isEditMode) {
      fetchClient(id)
        .catch((error) => {
          showError('Failed to fetch client: ' + error.message);
          navigate('/clients');
        });
    }
  }, [isEditMode, id, fetchClient, showError, navigate]);
  
  // Set client data when currentClient changes (for edit mode)
  useEffect(() => {
    if (isEditMode && currentClient) {
      setClientData(currentClient);
    }
  }, [isEditMode, currentClient]);
  
  // Formik hook for form handling
  const formik = useFormik({
    initialValues: clientData,
    validationSchema: getValidationSchema(activeStep),
    enableReinitialize: true,
    // Key additions: validateOnMount and validateOnChange to improve error displays
    validateOnMount: true,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values) => {
      try {
        setSubmitting(true);
        
        // Create or update client
        let savedClient;
        if (isEditMode) {
          savedClient = await updateClient(id, values);
          showSuccess('Client updated successfully');
        } else {
          savedClient = await createClient(values);
          showSuccess('Client created successfully');
        }
        
        // Upload logo if provided
        if (logoFile && savedClient) {
          await uploadLogo(savedClient._id, logoFile);
        }
        
        // Navigate to the client detail page
        navigate(`/clients/${savedClient._id}`);
      } catch (error) {
        showError(isEditMode 
          ? 'Failed to update client: ' + error.message 
          : 'Failed to create client: ' + error.message
        );
        setSubmitting(false);
      }
    }
  });
  
  // Field change handlers
  const handleNestedChange = (parent, field, value) => {
    formik.setFieldValue(`${parent}.${field}`, value);
  };
  
  const handleContactsChange = (contacts) => {
    formik.setFieldValue('contacts', contacts);
  };
  
  const handleLogoUpload = (file) => {
    setLogoFile(file);
  };
  
  const handleLogoRemove = () => {
    setLogoFile(null);
    formik.setFieldValue('logo', null);
  };
  
  // Handle form submission from stepper
  const handleStepperSubmit = () => {
    formik.handleSubmit();
  };
  
  // Handle step change
  const handleStepChange = (newStep) => {
    setActiveStep(newStep);
  };
  
  // Step validation function
  const validateStep = async (stepIndex) => {
    try {
      // Mark ALL required fields as touched first (this forces error display)
      if (stepTouchFields[stepIndex]) {
        await stepTouchFields[stepIndex]();
      }

      // Force a validation run on all fields in the step
      // This is the key change - we set all fields as touched regardless of user interaction
      const schema = getValidationSchema(stepIndex);
      try {
        await schema.validate(formik.values, { abortEarly: false });
      } catch (validationError) {
        // Make sure ALL fields with errors are marked as touched
        if (validationError.inner && validationError.inner.length > 0) {
          // This ensures each field with an error is marked as touched
          validationError.inner.forEach(err => {
            formik.setFieldTouched(err.path, true, false);
          });
          
          // Wait for touch operations to complete
          await new Promise(resolve => setTimeout(resolve, 50));
          
          return false;
        }
      }

      // Additional check for any errors in the current step's fields
      const hasErrors = Object.keys(formik.errors).some(key => {
        if (stepIndex === 0 && ['name', 'industry', 'region', 'status', 'subscriptionTier'].includes(key)) {
          return true;
        } else if (stepIndex === 1 && ['contacts', 'address'].includes(key)) {
          return true;
        }
        return false;
      });

      // If there are errors, make sure all fields are touched to show the errors
      if (hasErrors) {
        if (stepIndex === 0) {
          ['name', 'industry', 'region', 'status', 'subscriptionTier'].forEach(field => {
            formik.setFieldTouched(field, true, false);
          });
        } else if (stepIndex === 1) {
          formik.setFieldTouched('contacts[0].name', true, false);
          formik.setFieldTouched('contacts[0].email', true, false);
        }
        
        await new Promise(resolve => setTimeout(resolve, 50));
        return false;
      }

      return true;
    } catch (error) {
      console.error('Step validation error:', error);
      return false;
    }
  };

  // Add touch fields function for each step - now with async/await for better reliability
  const stepTouchFields = {
    0: async () => {
      // Mark all basic info fields as touched to show errors
      ['name', 'industry', 'region', 'status', 'subscriptionTier', 'description', 'code'].forEach(field => {
        formik.setFieldTouched(field, true, false);
      });
      
      // Run validation right away for immediate feedback
      await formik.validateForm();
    },
    1: async () => {
      // Mark all contact fields as touched
      formik.setFieldTouched('contacts', true, false);
      formik.setFieldTouched('contacts[0]', true, false);
      formik.setFieldTouched('contacts[0].name', true, false);
      formik.setFieldTouched('contacts[0].email', true, false);
      formik.setFieldTouched('contacts[0].position', true, false);
      formik.setFieldTouched('contacts[0].phone', true, false);
      
      // Mark address fields as touched
      ['address', 'address.street', 'address.city', 'address.state', 'address.zipCode', 'address.country'].forEach(field => {
        formik.setFieldTouched(field, true, false);
      });
      
      // Run validation right away for immediate feedback
      await formik.validateForm();
    },
    2: async () => {
      // No required fields in this step, but still run validation
      await formik.validateForm();
    }
  };
  
  // Breadcrumbs for header
  const breadcrumbs = [
    { label: 'Clients', path: '/clients' },
    { label: isEditMode ? `Edit ${currentClient?.name || 'Client'}` : 'Create New Client' }
  ];
  
  // Loading state - this needs to be before we create steps array
  if ((isLoading && isEditMode && !currentClient) || !formik) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
        <MagicLoader variant="mystical" size="large" message="Loading client data..." />
      </Box>
    );
  }
  
  // Steps for the stepper - create this AFTER formik is initialized
  const steps = [
    {
      label: 'Basic Information',
      icon: <BusinessIcon />,
      optional: false,
      touchFields: stepTouchFields[0],
      description: 'Enter essential details about your client',
      component: (
        <FormStep
          label="Basic Information"
          description="Enter essential details about your client"
          icon={<BusinessIcon />}
        >
          <ClientBasicInfoForm
            data={formik.values}
            onChange={formik.handleChange}
            setFieldValue={formik.setFieldValue}
            errors={formik.errors}
            touched={formik.touched}
            clientData={formik.values} // For backward compatibility
          />
        </FormStep>
      )
    },
    {
      label: 'Contact Information',
      icon: <ContactPhoneIcon />,
      optional: false,
      touchFields: stepTouchFields[1],
      description: 'Add contact details and address information',
      component: (
        <FormStep
          label="Contact Information"
          description="Add contact details and address information"
          icon={<ContactPhoneIcon />}
        >
          <ClientContactForm
            data={formik.values}
            onChange={formik.handleChange}
            onNestedChange={handleNestedChange}
            onContactsChange={handleContactsChange}
            setFieldValue={formik.setFieldValue}
            errors={formik.errors}
            touched={formik.touched}
            clientData={formik.values} // For backward compatibility
          />
        </FormStep>
      )
    },
    {
      label: 'Magical Elements',
      icon: <MagicIcon />,
      optional: true,
      touchFields: stepTouchFields[2],
      description: 'Enhance your client with magical elements and logo',
      component: (
        <FormStep
          label="Magical Elements"
          description="Enhance your client with magical elements and logo"
          icon={<MagicIcon />}
        >
          <ClientMagicalElementsForm
            data={formik.values}
            onChange={formik.handleChange}
            onNestedChange={handleNestedChange}
            setFieldValue={formik.setFieldValue}
            errors={formik.errors}
            touched={formik.touched}
            onLogoUpload={handleLogoUpload}
            onLogoRemove={handleLogoRemove}
            logo={logoFile || formik.values.logo}
            clientData={formik.values} // For backward compatibility
          />
        </FormStep>
      )
    },
  ];
  
  return (
    <Box sx={{ py: 3, flexGrow: 1, overflow: 'auto', height: '85vh' }}>
      {/* Page Header with Breadcrumbs */}
      <FormPageHeader
        title={isEditMode ? `Edit ${currentClient?.name || 'Client'}` : 'Create New Client'}
        subtitle={isEditMode 
          ? 'Update client information and configuration'
          : 'Add a new client to your management portfolio'
        }
        breadcrumbs={breadcrumbs}
        entityType="Client"
        isEditMode={isEditMode}
      />
      
      {/* Error message */}
      {error && (
        <Card
          elevation={0}
          sx={{ 
            bgcolor: alpha(theme.palette.error.main, 0.1),
            color: theme.palette.error.main,
            p: 2,
            mb: 3
          }}
        >
          <Typography variant="body2">
            {error}
          </Typography>
        </Card>
      )}
      
      {/* Form with Enhanced Stepper */}
      <FormStepperEnhanced
        steps={steps}
        activeStep={activeStep}
        onStepChange={handleStepChange}
        onSubmit={handleStepperSubmit}
        validateStep={validateStep}
        submitButtonText={isEditMode ? 'Update Client' : 'Create Client'}
        submitButtonIcon={<SaveIcon />}
        submitting={submitting}
        cancelLink="/clients"
        magical={true}
        nextButtonText="Next"
        backButtonText="Back"
        formik={formik} // Pass formik for better integration
      />
    </Box>
  );
};

export default ClientCreateEdit;